/**
 *
 *  Organizations/Show/OptIns
 *
 */

import React, { useState } from 'react'

// NPM Libraries
import Toggle from 'react-toggle'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

// GraphQL
import useQuery from 'components/UseQuery'
import { useMutation, gql } from '@apollo/client'

// Components
import Modal from 'components/Modal'
import Table from 'components/Table'
import Show from './Show'

// Utils
import { OptIn } from 'globalVars'
import { COLORS } from 'global-styles'

const ToggleContainer = styled.div`
	display: flex;
	justify-content: center;
	margin-left: -7px;

	.react-toggle {
		transform: scale(0.7);
	}
`

const DefaultOptionItem = styled.div`
	display: flex;
	justify-content: space-between;
	padding: 10px 15px;
	cursor: pointer;

	&:hover {
		background-color: ${COLORS.lightGrey};
	}
`

const GET_OPT_INS = gql`
	query organization_opt_ins($id: ID) {
		organization(id: $id) {
			id
			opt_in_id
			opt_in_default_behaviors
			opt_ins {
				id
				name
				text
				global_default
				available
			}
		}
	}
`

const UPDATE_OPT_IN = gql`
	mutation updateOptIn($id: ID!, $available: Boolean) {
		updateOptIn(input: { id: $id, available: $available }) {
			opt_in {
				id
				name
				text
			}
		}
	}
`

const DELETE_OPT_IN = gql`
	mutation deleteOptIn($id: ID!) {
		deleteOptIn(input: { id: $id }) {
			opt_in {
				id
			}
		}
	}
`

const SET_ORGANIZATION_DEFAULT_OPT_IN = gql`
	mutation updateOrganization($id: ID!, $opt_in_id: ID, $opt_in_default_behaviors: JSON) {
		updateOrganization(
			input: { id: $id, opt_in_id: $opt_in_id, opt_in_default_behaviors: $opt_in_default_behaviors }
		) {
			organization {
				id
				name
			}
		}
	}
`

export const generateOptions = (opt_in) => {
	const options = [
		{ text: 'Opt-in is required for registration', value: OptIn.REQUIRED },
		{ text: 'Allow campaign communication; block custom emails', value: OptIn.BLOCK_ALL_AUTOMATIONS },
		{
			text: 'Allow campaign communication; block custom emails about the campaign topic',
			value: OptIn.BLOCK_CAMPAIGN_TOPIC,
		},
		{ text: 'Block all emails and text messages', value: OptIn.BLOCK_ALL_COMMUNICATION },
	]

	if (!opt_in.global_default) {
		options.push({ text: 'Proceed as normal', value: 0 })
	}

	return options
}

function OptIns({ organization_id }) {
	const [show_form, setShowForm] = useState(null)
	const [show_select_default, setShowSelectDefault] = useState(null)

	const {
		data: { organization },
		loading,
		refetch,
	} = useQuery(GET_OPT_INS, {
		variables: { id: organization_id },
		channels: [organization_id],
		default_data: {
			organization: {},
		},
	})

	const [updateOptIn, { loading: updating }] = useMutation(UPDATE_OPT_IN, {
		onCompleted: () => {
			refetch()
		},
	})

	const [deleteOptIn] = useMutation(DELETE_OPT_IN, {
		onCompleted: () => {
			refetch()
		},
	})

	const [updateOrganization, { loading: updating_organization }] = useMutation(SET_ORGANIZATION_DEFAULT_OPT_IN)

	const columns = [
		{
			id: 'available',
			Header: 'Publish',
			accessor: (row) =>
				!row.global_default ? (
					<ToggleContainer onClick={(e) => e.stopPropagation()}>
						<Toggle
							value={row.available}
							checked={row.available}
							onChange={() => updateOptIn({ variables: { id: row.id, available: !row.available } })}
							disabled={updating || organization.opt_in_id === row.id}
						/>
					</ToggleContainer>
				) : null,
			width: 110,
			sortable: false,
		},
		{
			id: 'organization_default',
			Header: 'Default',
			accessor: (row) => {
				if (!row.available) return null

				const is_default = organization.opt_in_id === row.id

				return (
					<div className={`text-center ${is_default ? '' : 'hidden-cell'}`}>
						<FontAwesomeIcon
							onClick={() =>
								!updating_organization &&
								updateOrganization({
									variables: {
										id: organization.id,
										opt_in_id: !is_default ? row.id : null,
									},
								})
							}
							icon="check-circle"
							size="lg"
							className={`clickable text-${is_default ? 'success' : 'secondary'}`}
						/>
					</div>
				)
			},
			width: 100,
			sortable: false,
		},
		{
			Header: 'Opt-In Name',
			accessor: 'name',
			sortable: false,
		},
		{
			Header: 'Opt-In Statement',
			accessor: 'text',
			sortable: false,
		},
		{
			id: 'handle_opt_in',
			Header: 'If a Contact DOES NOT Accept the Opt-In',
			accessor: (row) => {
				const options = generateOptions(row)

				const selected_option = options.find(
					(option) => option.value === organization.opt_in_default_behaviors[row.id],
				)

				return (
					<div>
						<button onClick={() => setShowSelectDefault(row)} className="btn btn-link p-0">
							{selected_option?.text}
						</button>
					</div>
				)
			},
			sortable: false,
		},
		{
			id: 'actions',
			minWidth: 170,
			sortable: false,
			accessor: (row) => (
				<div className="text-right hidden-cell">
					<button
						className="btn btn-sm btn-primary"
						onClick={() => {
							setShowForm(row)
						}}
					>
						View
					</button>
					&nbsp;
					{!row.global_default && (
						<button
							className="btn btn-sm btn-danger"
							onClick={(e) => {
								e.stopPropagation()

								swal({
									title: 'Are you sure you want to delete this Opt-In?',
									type: 'warning',
									showCancelButton: true,
									confirmButtonText: 'Yes, delete this Opt-In!',
									cancelButtonText: 'Cancel',
									confirmButtonClass: 'btn btn-danger d-inline',
									cancelButtonClass: 'btn btn-default d-inline',
									buttonsStyling: false,
								}).then(
									() => deleteOptIn({ variables: { id: row.id } }),
									() => {},
								)
							}}
						>
							Delete
						</button>
					)}
				</div>
			),
		},
	]

	const global_default_opt_ins = organization.opt_ins?.filter((opt_in) => opt_in.global_default)
	const custom_opt_ins = organization.opt_ins?.filter((opt_in) => !opt_in.global_default)

	const new_opt_ins_list = [...(custom_opt_ins || []), ...(global_default_opt_ins || [])]

	return (
		<div className="container-fluid p-0" style={{ opacity: loading ? '0.5' : 1 }}>
			<h3>Contact Opt-In Statements</h3>

			<p className="mb-2">
				Opt-In statements can be added to the registration form on Landing Pages. Selecting the checkmark will
				set the corresponding Opt-In statement as default, which will automatically apply across all future
				Landing Pages for Users within the Organization. Only one Opt-In (or none) can be selected as the
				default.
			</p>

			<p>
				The switch adjacent to each Opt-In will publish it as an option on the Campaign level for all Users
				within the Organization. Publishing does not automatically apply the Opt-In to Landing Pages. Instead,
				Users within the Organization will see the Opt-In statement as an available option within the “Edit
				Campaign” page.
			</p>

			<div className="mb-3">
				<button className="btn btn-primary" onClick={() => setShowForm({})}>
					<FontAwesomeIcon icon="plus" style={{ marginBottom: '-1px' }} /> Create New Custom Opt-In
				</button>
			</div>

			<Table data={new_opt_ins_list} columns={columns} loading={loading} noDataText="No Opt-Ins Found" />

			{!!show_form && organization && (
				<Modal show modalDidClose={() => setShowForm(null)} style={{ width: '500px', padding: '25px' }}>
					<Show
						opt_in={{
							organization_id,
							name: `Organization Custom Opt-In ${custom_opt_ins?.length + 1}`,
							...show_form,
						}}
						organization={organization}
						handleClose={() => setShowForm(null)}
					/>
				</Modal>
			)}

			{!!show_select_default && organization && (
				<Modal
					show
					modalDidClose={() => setShowSelectDefault(null)}
					style={{ width: '700px', padding: '25px' }}
				>
					<h5 className="mb-3">
						If a contact does <em>not</em> accept the opt-in:
					</h5>

					<div className="border border-bottom-0">
						{generateOptions(show_select_default).map((option) => {
							const opt_in_selected =
								organization.opt_in_default_behaviors[show_select_default.id] === option.value

							return (
								<DefaultOptionItem
									key={option.value}
									onClick={() => {
										const new_opt_in_behaviors = { ...organization.opt_in_default_behaviors }
										new_opt_in_behaviors[show_select_default.id] = option.value

										updateOrganization({
											variables: {
												id: organization_id,
												opt_in_default_behaviors: new_opt_in_behaviors,
											},
										})
									}}
									className="border-bottom"
								>
									<span>{option.text}</span>

									{opt_in_selected && (
										<FontAwesomeIcon icon="check-circle" className="text-success" size="lg" />
									)}
								</DefaultOptionItem>
							)
						})}
					</div>
				</Modal>
			)}
		</div>
	)
}

OptIns.propTypes = {
	organization_id: PropTypes.string,
}

export default OptIns
